<template>
  <div class="pt-4 px-4">
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    ></v-progress-linear>

    <div v-if="!loading">
      <v-layout row align-center class="mb-4" wrap>
        <v-flex xs12 sm5>
          <div class="display-1 font-weight-bold">Bus Assignments</div>
          <div class="grey--text mt-1">
            Students assigned to routes, published from route designs or added
            manually
          </div>
        </v-flex>
        <v-flex
          xs12
          sm7
          style="display:flex;align-items:center;justify-content:flex-end;gap:12px"
        >
          <v-select
            v-model="kind"
            :items="kindOptions"
            label="Kind"
            dense
            outlined
            hide-details
            style="max-width:140px"
            @change="onKindChange"
          ></v-select>
          <v-autocomplete
            v-model="selectedTerm"
            :items="terms"
            label="Term"
            dense
            outlined
            hide-details
            style="max-width:160px"
            @change="onTermChange"
          ></v-autocomplete>
          <v-autocomplete
            v-model="selectedRouteIds"
            :items="routeOptions"
            :item-text="routeSearchText"
            item-value="id"
            label="Routes"
            dense
            outlined
            hide-details
            multiple
            clearable
            style="max-width:320px"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index < 2" small close @input="removeRouteFilter(item.id)">
                {{ routeTitle(item) }}
              </v-chip>
              <span v-else-if="index === 2" class="caption grey--text">
                +{{ selectedRouteIds.length - 2 }}
              </span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{ routeTitle(item) }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ routeDetails(item) }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-btn
            color="success"
            outline
            @click="$router.push({ name: 'RouteDesigns' })"
          >
            <v-icon left>mdi-map-marker-distance</v-icon>
            Route Designs
          </v-btn>
          <v-btn color="success" :disabled="!selectedTerm" @click="openAdd">
            <v-icon left>mdi-plus</v-icon>
            Add
          </v-btn>
        </v-flex>
      </v-layout>

      <v-card elevation="2">
        <v-card-title class="pt-2 pb-0">
          <v-btn
            color="error"
            outline
            :disabled="selected.length === 0"
            @click="deleteDialog = true"
          >
            <v-icon left small>mdi-delete</v-icon>
            Remove ({{ selected.length }})
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn
                color="primary"
                outline
                :loading="downloading"
                :disabled="!filteredRows.length"
                v-on="on"
              >
                <v-icon left small>mdi-printer</v-icon>
                Print {{ selected.length ? "selected (" + selected.length + ")" : "" }}
                <v-icon right small>mdi-menu-down</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-tile @click="download('bus')">
                <v-list-tile-title>Bus report (by stop)</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="download('class')">
                <v-list-tile-title>Section report</v-list-tile-title>
              </v-list-tile>
              <v-list-tile @click="download('labels')">
                <v-list-tile-title>Labels (Avery 5160)</v-list-tile-title>
              </v-list-tile>
            </v-list>
          </v-menu>
          <span class="grey--text ml-3">
            {{ filteredRows.length }} assignment{{
              filteredRows.length === 1 ? "" : "s"
            }}
            · {{ studentCount }} student{{ studentCount === 1 ? "" : "s" }}
          </span>
        </v-card-title>
        <div class="pa-2 assignments-grid">
          <DxDataGrid
            ref="grid"
            :data-source="filteredRows"
            key-expr="id"
            :show-borders="false"
            :row-alternation-enabled="true"
            :allow-column-resizing="true"
            :allow-column-reordering="true"
            :column-auto-width="true"
            :word-wrap-enabled="true"
            :hover-state-enabled="true"
            no-data-text="No bus assignments for this filter"
            @selection-changed="onGridSelectionChanged"
            @content-ready="fitSelectColumn"
          >
            <DxSelection mode="multiple" show-check-boxes-mode="always" select-all-mode="allPages" />
            <DxColumn type="selection" :width="32" />
            <DxFilterRow :visible="true" />
            <DxHeaderFilter :visible="true" />
            <DxSearchPanel :visible="true" :width="260" placeholder="Search..." />
            <DxExport
              :enabled="true"
              :allow-export-selected-data="true"
              :file-name="exportFileName"
            />
            <DxColumnChooser :enabled="true" />
            <DxSorting mode="multiple" />
            <DxPaging :page-size="50" />
            <DxPager
              :show-page-size-selector="true"
              :allowed-page-sizes="[25, 50, 100, 500]"
              :show-info="true"
            />

            <DxColumn data-field="studentID" caption="Student ID" :width="100" />
            <DxColumn data-field="hebName" caption="Name" cell-template="nameCell" />
            <DxColumn data-field="name" caption="English name" :visible="false" />
            <DxColumn data-field="classSection" caption="Class" :width="90" />
            <DxColumn data-field="building" caption="Building" />
            <DxColumn data-field="busAddress" caption="Address" />
            <DxColumn
              data-field="busText"
              caption="Bus"
              cell-template="busCell"
              :sort-index="0"
              sort-order="asc"
            />
            <DxColumn data-field="routeLabel" caption="Route name" :visible="false" />
            <DxColumn data-field="driver" caption="Driver" :visible="false" />
            <DxColumn
              data-field="stopNumber"
              caption="Stop #"
              data-type="number"
              alignment="center"
              :width="80"
              :sort-index="1"
              sort-order="asc"
            />
            <DxColumn data-field="stopName" caption="Stop" />
            <DxColumn data-field="source" caption="Source" cell-template="sourceCell" />
            <DxColumn
              data-field="updated"
              caption="Updated"
              data-type="date"
              format="shortDate"
              cell-template="updatedCell"
            />
            <DxColumn data-field="updatedByText" caption="Updated by" :visible="false" />
            <DxColumn data-field="notes" caption="Notes" :visible="false" />
            <DxColumn
              caption=""
              cell-template="editCell"
              :width="50"
              :allow-filtering="false"
              :allow-sorting="false"
              :allow-exporting="false"
              :allow-header-filtering="false"
            />

            <template #nameCell="{ data }">
              <div>
                <div class="font-weight-medium">{{ data.data.hebName }}</div>
                <div class="caption grey--text">{{ data.data.name }}</div>
              </div>
            </template>
            <template #busCell="{ data }">
              <div class="bus-cell">
                <span class="bus-cell__dot" :style="{ background: data.data.routeColor }"></span>
                <div>
                  <div>
                    <span class="font-weight-bold">{{ data.data.busTitle }}</span>
                    <span v-if="data.data.timeStart" class="bus-cell__time">{{ data.data.timeStart }}</span>
                  </div>
                  <div v-if="data.data.busSubtitle" class="caption grey--text">
                    {{ data.data.busSubtitle }}
                  </div>
                </div>
              </div>
            </template>
            <template #sourceCell="{ data }">
              <router-link
                v-if="data.data.routeDesignID"
                :to="{ name: 'RouteDesignMap', params: { id: String(data.data.routeDesignID) } }"
                >{{ data.data.source }}</router-link
              >
              <span v-else class="grey--text">Manual</span>
            </template>
            <template #updatedCell="{ data }">
              <div class="caption">
                {{ formatDate(data.data.updated) }}
                <div class="grey--text">{{ data.data.updatedByText }}</div>
              </div>
            </template>
            <template #editCell="{ data }">
              <v-btn icon small class="ma-0" title="Edit" @click="openEdit(data.data)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </DxDataGrid>
        </div>
      </v-card>
    </div>

    <!-- ADD: pick students in a grid, then the route and a design stop -->
    <v-dialog v-model="addDialog" max-width="1300" persistent scrollable>
      <v-card>
        <v-card-title class="headline success white--text pa-3">
          Add Bus Assignment
          <v-spacer></v-spacer>
          <span class="subheading">{{ selectedTerm }}</span>
        </v-card-title>
        <v-card-text class="pa-0">
          <div class="add-layout">
            <div class="add-layout__grid">
              <v-progress-linear
                v-if="termStudentsLoading"
                indeterminate
                color="success"
                class="ma-0"
              ></v-progress-linear>
              <DxDataGrid
                ref="studentGrid"
                :data-source="termStudents"
                key-expr="studentID"
                :show-borders="true"
                :row-alternation-enabled="true"
                :allow-column-resizing="true"
                :column-auto-width="true"
                :height="560"
                no-data-text="No students for this term"
                @selection-changed="onStudentSelectionChanged"
              >
                <DxSelection mode="multiple" show-check-boxes-mode="always" select-all-mode="page" />
                <DxFilterRow :visible="true" />
                <DxHeaderFilter :visible="true" />
                <DxSearchPanel :visible="true" :width="240" placeholder="Search students..." />
                <DxSorting mode="multiple" />
                <DxPaging :page-size="50" />
                <DxPager :show-info="true" />
                <DxScrolling mode="standard" />

                <DxColumn data-field="studentID" caption="ID" :width="90" />
                <DxColumn data-field="hebName" caption="Name" />
                <DxColumn data-field="name" caption="English name" />
                <DxColumn data-field="classSection" caption="Class" :width="80" />
                <DxColumn data-field="building" caption="Building" />
                <DxColumn data-field="busAddress" caption="Address" />
                <DxColumn data-field="currentBuses" caption="Current bus" cell-template="currentBusCell" />

                <template #currentBusCell="{ data }">
                  <span
                    v-if="data.data.currentBuses"
                    :class="addForm.routeID && data.data.routeIDs.indexOf(addForm.routeID) !== -1
                      ? 'orange--text text--darken-2 font-weight-medium'
                      : ''"
                    >{{ data.data.currentBuses }}</span
                  >
                  <span v-else class="grey--text">—</span>
                </template>
              </DxDataGrid>
            </div>
            <div class="add-layout__side">
              <div class="subheading font-weight-medium mb-1">
                {{ pickedStudentIds.length }} student{{ pickedStudentIds.length === 1 ? "" : "s" }} selected
              </div>
              <div class="picked-list mb-3">
                <div v-for="s in pickedStudents.slice(0, 8)" :key="s.studentID" class="caption">
                  {{ s.hebName || s.name }} <span class="grey--text">· {{ s.classSection }}</span>
                </div>
                <div v-if="pickedStudents.length > 8" class="caption grey--text">
                  and {{ pickedStudents.length - 8 }} more
                </div>
                <div v-if="!pickedStudents.length" class="caption grey--text">
                  Check students in the grid. Use the filter row and header filters to narrow the list.
                </div>
              </div>
              <v-autocomplete
                v-model="addForm.routeID"
                :items="routeItems"
                :item-text="routeSearchText"
                item-value="id"
                label="Bus / route *"
                outlined
                dense
                @change="onAddRouteChange"
              >
                <template v-slot:selection="{ item }">
                  <span class="font-weight-medium">{{ routeTitle(item) }}</span>
                  <span class="caption grey--text ml-2 text-truncate">{{ routeDetails(item) }}</span>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-tile-content>
                    <v-list-tile-title class="font-weight-medium">{{ routeTitle(item) }}</v-list-tile-title>
                    <v-list-tile-sub-title>{{ routeDetails(item) }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </template>
              </v-autocomplete>
              <v-select
                v-model="addForm.stopKey"
                :items="stopItems"
                item-text="text"
                item-value="key"
                label="Stop"
                :loading="stopsLoading"
                :disabled="!addForm.routeID"
                :hint="stopHint"
                persistent-hint
                outlined
                dense
              >
                <template v-slot:item="{ item }">
                  <v-list-tile-content>
                    <v-list-tile-title>{{ item.text }}</v-list-tile-title>
                    <v-list-tile-sub-title v-if="item.sub">{{ item.sub }}</v-list-tile-sub-title>
                  </v-list-tile-content>
                </template>
              </v-select>
              <v-textarea
                v-model="addForm.notes"
                label="Notes"
                rows="2"
                auto-grow
                outlined
                dense
                class="mt-3"
              ></v-textarea>
              <div v-if="pickedAlreadyOnRoute" class="caption orange--text text--darken-2">
                {{ pickedAlreadyOnRoute }} selected student(s) are already on this bus and
                will be skipped.
              </div>
              <v-alert :value="!!formError" type="error" class="mt-3" @input="formError = ''">
                {{ formError }}
              </v-alert>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="addDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="saving"
            :disabled="!pickedStudentIds.length || !addForm.routeID"
            @click="saveMany"
          >
            Add {{ pickedStudentIds.length || "" }} student{{ pickedStudentIds.length === 1 ? "" : "s" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- EDIT -->
    <v-dialog v-model="editDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline success white--text pa-3">
          Edit Bus Assignment
        </v-card-title>
        <v-card-text class="pt-3">
          <div class="mb-3">
            <div class="subheading font-weight-medium">{{ form.hebName || form.name }}</div>
            <div class="caption grey--text">
              {{ [form.studentID, form.classSection, form.busAddress].filter(Boolean).join(" · ") }}
            </div>
          </div>
          <v-autocomplete
            v-model="form.routeID"
            :items="routeItems"
            :item-text="routeSearchText"
            item-value="id"
            label="Bus / route *"
            outlined
            dense
            @change="onEditRouteChange"
          >
            <template v-slot:selection="{ item }">
              <span class="font-weight-medium">{{ routeTitle(item) }}</span>
              <span class="caption grey--text ml-2 text-truncate">{{ routeDetails(item) }}</span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title class="font-weight-medium">{{ routeTitle(item) }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ routeDetails(item) }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
          <v-select
            v-model="form.stopKey"
            :items="stopItems"
            item-text="text"
            item-value="key"
            label="Stop"
            :loading="stopsLoading"
            :disabled="!form.routeID"
            :hint="stopHint"
            persistent-hint
            outlined
            dense
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title>{{ item.text }}</v-list-tile-title>
                <v-list-tile-sub-title v-if="item.sub">{{ item.sub }}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-select>
          <v-textarea
            v-model="form.notes"
            label="Notes"
            rows="2"
            auto-grow
            outlined
            dense
            class="mt-3"
          ></v-textarea>
          <div v-if="form.routeDesignID" class="caption grey--text">
            Published from route design "{{ form.routeDesignName }}". Republishing
            that design overwrites the route for this student.
          </div>
          <v-alert
            :value="!!formError"
            type="error"
            class="mt-3"
            @input="formError = ''"
          >
            {{ formError }}
          </v-alert>
        </v-card-text>
        <v-card-actions class="pb-3 pr-0">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="editDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            :loading="saving"
            :disabled="!form.routeID"
            @click="save"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Remove assignments?</v-card-title>
        <v-card-text>
          Remove {{ selected.length }} selected bus
          {{ selected.length === 1 ? "assignment" : "assignments" }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="deleteDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="error" :loading="deleting" @click="doDelete">
            Remove
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="4000" color="success" bottom>
      {{ snackbarText }}
    </v-snackbar>

    <v-alert
      :value="!!errorMessage"
      type="error"
      @input="errorMessage = ''"
      class="mt-4"
    >
      {{ errorMessage }}
    </v-alert>
  </div>
</template>

<script>
import "devextreme/dist/css/dx.common.css";
import "devextreme/dist/css/dx.light.css";
import {
  DxDataGrid,
  DxColumn,
  DxColumnChooser,
  DxExport,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging,
  DxScrolling,
  DxSearchPanel,
  DxSelection,
  DxSorting,
} from "devextreme-vue/data-grid";
import {
  getStoredRouteKind,
  setStoredRouteKind,
  routeOptionTitle,
  routeOptionDetails,
} from "../../utils/helpers.js";

const CURRENT_STOP_KEY = "current";

const ROUTE_COLOR_NAMES = {
  BEIGE: "#F5F5DC",
  BLACK: "#000000",
  BLUE: "#0000FF",
  BROWN: "#8B4513",
  GOLD: "#FFD700",
  GREEN: "#008000",
  ORANGE: "#FFA500",
  PINK: "#FFC0CB",
  PURPLE: "#800080",
  RED: "#FF0000",
  SILVER: "#C0C0C0",
  WHITE: "#E0E0E0",
  YELLOW: "#FFFF00",
  "DARK BLUE": "#00008B",
  "DARK GREEN": "#006400",
  "DARK PINK": "#FF1493",
  "DARK PURPLE": "#4B0082",
  "LIGHT BLUE": "#ADD8E6",
  "LIGHT GREEN": "#90EE90",
  "LIGHT PINK": "#FFB6C1",
  "LIGHT PURPLE": "#DDA0DD",
  GRAY: "#808080",
  GREY: "#808080",
};

function busDotColor(color) {
  const raw = (color || "").trim();
  if (!raw) return "#9e9e9e";
  if (raw.charAt(0) === "#") return raw;
  const named = ROUTE_COLOR_NAMES[raw.toUpperCase()];
  if (named) return named;
  return raw.indexOf(" ") === -1 ? raw : "#9e9e9e";
}

export default {
  name: "BusAssignments",
  components: {
    DxDataGrid,
    DxColumn,
    DxColumnChooser,
    DxExport,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    DxScrolling,
    DxSearchPanel,
    DxSelection,
    DxSorting,
  },
  data() {
    return {
      loading: true,
      terms: [],
      selectedTerm: "",
      kind: "Regular",
      kindOptions: [
        { text: "Regular", value: "Regular" },
        { text: "Day Camp", value: "DayCamp" },
      ],
      routeOptions: [],
      selectedRouteIds: [],
      rows: [],
      selected: [],

      addDialog: false,
      addForm: { routeID: null, stopKey: "", notes: "" },
      termStudents: [],
      termStudentsTerm: null,
      termStudentsLoading: false,
      pickedStudentIds: [],

      editDialog: false,
      form: {},

      routeStops: [],
      stopsLoading: false,
      saving: false,
      formError: "",
      deleteDialog: false,
      deleting: false,
      downloading: false,
      errorMessage: "",
      snackbar: false,
      snackbarText: "",
    };
  },
  computed: {
    filteredRows() {
      if (!this.selectedRouteIds.length) return this.rows;
      return this.rows.filter(
        (r) => this.selectedRouteIds.indexOf(r.routeID) !== -1
      );
    },
    routeItems() {
      const items = this.routeOptions.slice();
      const routeID = this.editDialog ? this.form.routeID : this.addForm.routeID;
      if (routeID && !items.some((r) => r.id === routeID)) {
        const row = this.rows.find((r) => r.routeID === routeID);
        items.unshift({
          id: routeID,
          label: row ? row.routeLabel : "Route " + routeID,
          busNumber: row ? row.busNumber : null,
          timeStart: row ? row.timeStart : null,
        });
      }
      return items;
    },
    studentCount() {
      const ids = {};
      this.filteredRows.forEach((r) => (ids[r.studentID] = true));
      return Object.keys(ids).length;
    },
    exportFileName() {
      return "BusAssignments_" + (this.selectedTerm || "");
    },
    pickedStudents() {
      const picked = new Set(this.pickedStudentIds);
      return this.termStudents.filter((s) => picked.has(s.studentID));
    },
    pickedAlreadyOnRoute() {
      const routeID = this.addForm.routeID;
      if (!routeID) return 0;
      return this.pickedStudents.filter((s) => s.routeIDs.indexOf(routeID) !== -1).length;
    },
    stopItems() {
      const multipleDesigns =
        new Set(this.routeStops.map((s) => s.routeDesignBusID)).size > 1;
      const items = [{ key: "", text: "No stop" }].concat(
        this.routeStops.map((s) => ({
          key: this.stopKey(s),
          text: "Stop " + s.stopNumber + (s.name ? " · " + s.name : ""),
          sub: multipleDesigns ? s.designName : null,
        }))
      );
      if (this.editDialog && this.form.stopKey === CURRENT_STOP_KEY) {
        items.push({
          key: CURRENT_STOP_KEY,
          text:
            "Stop " +
            this.form.stopNumber +
            (this.form.stopName ? " · " + this.form.stopName : ""),
          sub: "Current stop (not in a route design)",
        });
      }
      return items;
    },
    stopHint() {
      if (this.stopsLoading) return "";
      if (!this.routeStops.length) {
        return "This bus has no stops in a route design yet. Add stops in the route design first.";
      }
      return "Stops come from the route design for this bus.";
    },
  },
  async created() {
    try {
      this.loading = true;
      this.kind = getStoredRouteKind();
      this.terms = (await this.$http.get("api/allStudents/getRegTerms")) || [];
      const stored = localStorage.getItem("busAssignments.selectedTerm");
      this.selectedTerm =
        stored && this.terms.indexOf(stored) !== -1
          ? stored
          : this.terms.length
          ? this.terms[this.terms.length - 1]
          : "";
      await this.loadTermData();
    } catch (e) {
      console.error(e);
      this.errorMessage = "Failed to load bus assignments";
    } finally {
      this.loading = false;
    }
  },
  methods: {
    routeTitle: routeOptionTitle,
    routeDetails: routeOptionDetails,
    routeSearchText(route) {
      return [routeOptionTitle(route), route.label, route.driver].filter(Boolean).join(" ");
    },
    removeRouteFilter(id) {
      this.selectedRouteIds = this.selectedRouteIds.filter((r) => r !== id);
    },
    async onTermChange() {
      if (this.selectedTerm) {
        localStorage.setItem("busAssignments.selectedTerm", this.selectedTerm);
      }
      this.selectedRouteIds = [];
      await this.loadTermData();
    },
    async onKindChange() {
      setStoredRouteKind(this.kind);
      this.selectedRouteIds = [];
      this.routeOptions = (await this.loadRouteOptions()) || [];
    },
    loadRouteOptions() {
      return this.$http.get(
        "api/busAssignment/getRouteOptions?kind=" +
          encodeURIComponent(this.kind || "")
      );
    },
    async loadTermData() {
      const [routes] = await Promise.all([this.loadRouteOptions(), this.loadRows()]);
      this.routeOptions = routes || [];
    },
    async loadRows() {
      const rows =
        (await this.$http.get(
          "api/busAssignment/getList?term=" +
            encodeURIComponent(this.selectedTerm || "")
        )) || [];
      this.rows = rows.map((r) => this.decorateRow(r));
      this.selected = [];
      const grid = this.$refs.grid && this.$refs.grid.instance;
      if (grid) grid.clearSelection();
    },
    decorateRow(r) {
      const busTitle = r.busNumber ? "Bus " + String(r.busNumber).trim() : r.routeLabel;
      const updatedAt = r.updatedAt || r.createdAt;
      return {
        ...r,
        classSection: [r.class, r.section].filter(Boolean).join("-"),
        busTitle,
        busSubtitle: r.busNumber ? r.routeLabel : null,
        routeColor: busDotColor(r.routeColor),
        busText: [busTitle, r.timeStart].filter(Boolean).join(" · "),
        source: r.routeDesignID ? r.routeDesignName || "Route design" : "Manual",
        updated: updatedAt ? new Date(updatedAt) : null,
        updatedByText: r.updatedBy || r.createdBy || "",
      };
    },
    onGridSelectionChanged(e) {
      this.selected = e.selectedRowsData || [];
    },
    fitSelectColumn(e) {
      const grid = e && e.component;
      if (!grid || grid.columnOption("command:select", "width") === 32) return;
      grid.columnOption("command:select", "width", 32);
    },
    formatDate(val) {
      if (!val) return "";
      return new Date(val).toLocaleDateString();
    },

    stopKey(s) {
      return s.routeDesignBusID + ":" + s.stopNumber;
    },
    async loadRouteStops(routeID, routeDesignBusID) {
      this.routeStops = [];
      if (!routeID) return;
      this.stopsLoading = true;
      try {
        let url =
          "api/busAssignment/getRouteStops?term=" +
          encodeURIComponent(this.selectedTerm || "") +
          "&routeID=" +
          routeID;
        if (routeDesignBusID) url += "&routeDesignBusID=" + routeDesignBusID;
        this.routeStops = (await this.$http.get(url)) || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.stopsLoading = false;
      }
    },
    stopFields(key) {
      const stop = this.routeStops.find((s) => this.stopKey(s) === key);
      if (!stop) return { stopNumber: null, stopName: null, stopLat: null, stopLng: null };
      return {
        stopNumber: stop.stopNumber,
        stopName: stop.name || null,
        stopLat: stop.lat,
        stopLng: stop.lng,
      };
    },

    async openAdd() {
      this.addForm = {
        routeID: this.selectedRouteIds.length === 1 ? this.selectedRouteIds[0] : null,
        stopKey: "",
        notes: "",
      };
      this.pickedStudentIds = [];
      this.formError = "";
      this.addDialog = true;
      this.$nextTick(() => {
        const grid = this.$refs.studentGrid && this.$refs.studentGrid.instance;
        if (grid) grid.clearSelection();
      });
      this.loadRouteStops(this.addForm.routeID);
      if (this.termStudentsTerm !== this.selectedTerm) await this.loadTermStudents();
    },
    async loadTermStudents() {
      this.termStudentsLoading = true;
      try {
        const list =
          (await this.$http.get(
            "api/busAssignment/getTermStudents?term=" +
              encodeURIComponent(this.selectedTerm || "")
          )) || [];
        this.termStudents = list.map((s) => ({
          ...s,
          classSection: [s.class, s.section].filter(Boolean).join("-"),
        }));
        this.termStudentsTerm = this.selectedTerm;
      } catch (e) {
        console.error(e);
        this.formError = "Failed to load students";
      } finally {
        this.termStudentsLoading = false;
      }
    },
    onStudentSelectionChanged(e) {
      this.pickedStudentIds = e.selectedRowKeys || [];
    },
    onAddRouteChange(routeID) {
      this.addForm.stopKey = "";
      this.loadRouteStops(routeID);
    },
    async saveMany() {
      this.saving = true;
      this.formError = "";
      try {
        const res = await this.$http.post(
          "api/busAssignment/saveMany",
          {
            term: this.selectedTerm,
            routeID: this.addForm.routeID,
            studentIDs: this.pickedStudentIds,
            ...this.stopFields(this.addForm.stopKey),
            notes: this.addForm.notes || null,
          },
          { notifyError: false }
        );
        this.addDialog = false;
        this.termStudentsTerm = null;
        this.snackbarText =
          (res.added || 0) +
          " student(s) added" +
          (res.skipped ? ", " + res.skipped + " already on this bus" : "");
        this.snackbar = true;
        await this.loadRows();
      } catch (e) {
        this.formError = (e && e.message) || "Failed to save";
      } finally {
        this.saving = false;
      }
    },

    async openEdit(item) {
      this.form = { ...item, stopKey: "" };
      this.formError = "";
      this.editDialog = true;
      await this.loadRouteStops(item.routeID, item.routeDesignBusID);
      if (item.stopNumber == null) return;
      const match =
        this.routeStops.find(
          (s) =>
            s.stopNumber === item.stopNumber &&
            (!item.routeDesignBusID || s.routeDesignBusID === item.routeDesignBusID)
        ) || this.routeStops.find((s) => s.stopNumber === item.stopNumber);
      this.form.stopKey = match ? this.stopKey(match) : CURRENT_STOP_KEY;
    },
    onEditRouteChange(routeID) {
      this.form.stopKey = "";
      this.loadRouteStops(routeID);
    },
    async save() {
      this.saving = true;
      this.formError = "";
      const keepCurrent = this.form.stopKey === CURRENT_STOP_KEY;
      const stop = keepCurrent
        ? {
            stopNumber: this.form.stopNumber,
            stopName: this.form.stopName || null,
            stopLat: this.form.stopLat || null,
            stopLng: this.form.stopLng || null,
          }
        : this.stopFields(this.form.stopKey);
      try {
        await this.$http.post(
          "api/busAssignment/save",
          {
            id: this.form.id,
            studentID: this.form.studentID,
            term: this.selectedTerm,
            routeID: this.form.routeID,
            ...stop,
            stopAddress: keepCurrent ? this.form.stopAddress || null : null,
            notes: this.form.notes || null,
          },
          { notifyError: false }
        );
        this.editDialog = false;
        await this.loadRows();
      } catch (e) {
        this.formError = (e && e.message) || "Failed to save";
      } finally {
        this.saving = false;
      }
    },
    async download(kind) {
      this.downloading = true;
      try {
        const response = await this.$http.post(
          "api/busAssignment/download",
          {
            term: this.selectedTerm,
            kind,
            routeIDs: this.selectedRouteIds.length ? this.selectedRouteIds : null,
            assignmentIDs: this.selected.length
              ? this.selected.map((r) => r.id)
              : null,
          },
          { responseType: "blob" }
        );
        const names = { bus: "BusReport", class: "SectionBusReport", labels: "BusLabels" };
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(
          new Blob([response], { type: "application/pdf" })
        );
        link.download = names[kind] + "_" + this.selectedTerm + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloading = false;
      }
    },
    async doDelete() {
      this.deleting = true;
      try {
        await this.$http.post(
          "api/busAssignment/delete",
          this.selected.map((r) => r.id)
        );
        this.deleteDialog = false;
        await this.loadRows();
      } catch (e) {
        console.error(e);
        this.errorMessage = "Failed to remove assignments";
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style>
.assignments-grid .dx-datagrid-table .dx-row > td.dx-command-select {
  width: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  padding-left: 4px !important;
  padding-right: 4px !important;
}
.assignments-grid .dx-header-row > td {
  padding-top: 2px !important;
  padding-bottom: 2px !important;
  vertical-align: middle !important;
}
.assignments-grid .dx-header-row > td > .dx-datagrid-text-content,
.assignments-grid .dx-header-row .dx-column-indicators {
  vertical-align: middle !important;
}
</style>

<style scoped>
.bus-cell {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.bus-cell__dot {
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
}
.bus-cell__time {
  margin-left: 6px;
  padding: 0 6px;
  font-size: 12px;
  border-radius: 10px;
  background: #e8f5e9;
  color: #2e7d32;
}
.add-layout {
  display: flex;
  min-height: 560px;
}
.add-layout__grid {
  flex: 1 1 auto;
  min-width: 0;
  padding: 12px;
}
.add-layout__side {
  flex: 0 0 340px;
  padding: 16px;
  border-left: 1px solid #e0e0e0;
  background: #fafafa;
}
.picked-list {
  max-height: 170px;
  overflow-y: auto;
}
</style>
